import { Link } from "react-router-dom";
import type { Plan } from "@/types";

interface PlanCardProps {
  plan: Plan;
  /** Formats a numeric price into the display currency string. */
  formatPrice: (price: number) => string;
}

/**
 * A single pricing card — matches the reference layout: icon badge, name,
 * tagline, price with a validity line, a "what you can add" chip box, the
 * feature list and a call-to-action button.
 */
export default function PlanCard({ plan, formatPrice }: PlanCardProps) {
  return (
    <article
      id={`plan-${plan.slug}`}
      className={`relative flex h-full scroll-mt-24 flex-col rounded-lg border bg-background-50 p-6 md:p-7 ${
        plan.isPopular ? "border-primary-400 ring-1 ring-primary-200" : "border-background-200"
      }`}
    >
      {plan.isPopular && (
        <span className="absolute -top-3 left-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent-500 px-3 py-1 text-xs font-bold uppercase tracking-wider text-foreground-950">
          <i className="ri-star-fill text-[12px]"></i> Most popular
        </span>
      )}

      <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950">
        <i className={`${plan.icon || "ri-store-3-line"} text-xl`}></i>
      </span>

      <h3 className="mt-4 font-heading text-xl font-extrabold text-foreground-950">{plan.name}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{plan.tagline}</p>

      <div className="mt-5 flex items-end gap-1.5">
        <span className="font-heading text-3xl font-extrabold text-foreground-950">{formatPrice(plan.price)}</span>
        {plan.price > 0 && <span className="pb-1 text-sm text-foreground-500">/{plan.period}</span>}
      </div>
      {plan.validity && (
        <p className="mt-1 text-xs text-foreground-500">
          Listing validity: {plan.validity}{" "}
          <Link
            to="/terms"
            className="font-medium text-secondary-700 underline decoration-dotted underline-offset-2 transition-colors hover:text-primary-700 cursor-pointer"
          >
            (Conditions Apply)
          </Link>
        </p>
      )}

      {plan.chips && plan.chips.length > 0 && (
        <div className="mt-5 rounded-md border border-background-200 bg-background-100/70 p-3">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-foreground-500">What you can add</p>
          <div className="flex flex-wrap gap-1.5">
            {plan.chips.map((chip) => (
              <span
                key={chip}
                className="inline-flex items-center gap-1.5 rounded-full bg-background-50 px-2.5 py-1 text-[11px] font-semibold text-foreground-700"
              >
                <i className="ri-checkbox-blank-circle-fill text-[7px] text-secondary-500"></i>
                {chip}
              </span>
            ))}
          </div>
        </div>
      )}

      <ul className="mt-5 flex flex-1 flex-col gap-2.5 border-t border-background-200 pt-5">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-foreground-700">
            <i className="ri-check-line mt-0.5 text-base text-secondary-600"></i>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Link
        to="/list-your-business"
        className={`mt-6 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-5 py-3 text-sm font-semibold transition-colors cursor-pointer ${
          plan.isPopular
            ? "bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600"
            : "border border-background-300 text-foreground-800 hover:border-primary-300 hover:text-primary-700"
        }`}
      >
        {plan.ctaLabel || (plan.price === 0 ? "Get Started Free" : `Choose ${plan.name}`)}
        <i className="ri-arrow-right-line text-base"></i>
      </Link>
    </article>
  );
}